import { NextPage } from 'next'
import Image from 'next/image'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { useAuth } from '~/components/Auth/AuthProvider'
import { logout } from '~/components/Auth/Logout'
import { BusinessAction } from '~/components/Business/BusinessAction'
import { BusinessCard } from '~/components/Business/BusinessCard'
import { Button } from '~/components/Common/Button'
import { Spacer } from '~/components/Common/Spacer'
import { Tooltip } from '~/components/Common/Tooltip'
import { DropdownWrapper } from '~/components/Dropdown/DropdownWrapper'
import Icon from '~/components/Icon'
import { Modal } from '~/components/Modal/Modal'
import { HeaderTitleBackButtonWrapper } from '~/components/Wrapper/HeaderTitleBackButtonWrapper'
import { HeaderWrapper } from '~/components/Wrapper/HeaderWrapper'
import locale from '~/locale'
import { RootState } from '~/redux/store'

const BusinessPage: NextPage = () => {
    const { businesses } = useAuth()

    const language = useSelector((state: RootState) => state.language.language)
    const localeText = (locale[language as keyof typeof locale] || locale.en) as any

    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
    return (
        <>
            <div className="flex justify-center items-center w-full h-full">
                <div className="w-full h-full flex flex-col">
                    <HeaderWrapper>
                        <div className="flex max-w-3xl w-full mx-auto">
                            <HeaderTitleBackButtonWrapper
                                title={localeText?.business?.title}
                                showBackButton={false}></HeaderTitleBackButtonWrapper>
                             <Spacer></Spacer>
                            <div 
                                onClick={() => setIsLogoutModalOpen(true)}
                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-red-50 rounded-full transition-all duration-200 cursor-pointer border border-neutrals-100 group shadow-sm bg-white">
                                <Icon name="logout" size="md" className="text-red-500 group-hover:scale-110 transition-transform"></Icon>
                                <span className="text-[13px] font-bold text-red-500 whitespace-nowrap">{localeText?.common?.logout}</span>
                            </div>
                        </div>
                    </HeaderWrapper>
                    <div className="w-full flex-grow flex flex-col items-center overflow-auto">
                        <div className="p-6 w-full bg-neutrals-light flex-grow flex flex-col">
                            <div className="flex-grow mb-6 overflow-auto border-b border-neutrals-100">
                                {businesses.length === 0 ? (
                                    <div key="empty-state" className="mx-auto max-w-3xl justify-center items-center w-full h-full flex flex-col text-lg text-neturals-900">
                                        <Image
                                            src="/images/business/no-business.svg"
                                            width={200}
                                            height={200}
                                            unoptimized
                                            alt={localeText?.business?.no_business}></Image>
                                        <p className="mt-5 capitalize ">{localeText?.business?.no_business}</p>
                                        <p className="">{localeText?.business?.create_or_join}.</p>
                                    </div>
                                ) : (
                                    <div key="business-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 pb-12 mx-auto max-w-3xl">
                                        {businesses.map((item, key) => (
                                            <BusinessCard
                                                business={item}
                                                key={item?.businessId || key}
                                                type="listing"
                                                index={key}
                                                localeText={localeText}></BusinessCard>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <BusinessAction localeText={localeText}></BusinessAction>
                        </div>
                    </div>
                </div>
            </div>

            <a href="https://wa.me/+919929970571" target="_blank" rel="noreferrer">
                <div
                    className="fixed right-6 bottom-6 rounded-full bg-green-400 hover:bg-green-500 transition-colors text-neutrals-light flex items-center justify-center p-3"
                    role="button">
                    <Tooltip content={'Chat on WhatsApp'} variant="information">
                        <div className="flex items-center justify-center">
                            <Icon width={32} name="chat-outline"></Icon>
                        </div>
                    </Tooltip>
                </div>
            </a>

            <Modal
                show={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                size="sm"
                showTopLine={true}
                header={false}
                acitonJustify="center"
                action={
                    <div className="flex gap-3 w-full">
                        <div className="flex-1">
                            <Button
                                fullWidth
                                text={localeText?.common?.button?.cancel}
                                type="outlined"
                                onClick={() => setIsLogoutModalOpen(false)}></Button>
                        </div>
                        <div className="flex-1">
                            <Button
                                fullWidth
                                text={localeText?.common?.logout}
                                type="error"
                                onClick={logout}></Button>
                        </div>
                    </div>
                }>
                <div className="flex flex-col items-center text-center p-6">
                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6 mt-2">
                        <Icon name="logout" size="xl" className="text-red-600" width={32} height={32}></Icon>
                    </div>
                    <h3 className="text-[#1a1916] font-bold text-xl mb-2">
                        {localeText?.common?.logout}
                    </h3>
                    <p className="text-[#4a4944] font-medium text-[15px] leading-relaxed">
                        {localeText?.common?.confirm_logout || 'Are you sure you want to logout?'}
                    </p>
                    <p className="text-[#8a8981] mt-2 text-sm">
                        {localeText?.common?.logout_description || 'You will need to login again to access your businesses.'}
                    </p>
                </div>
            </Modal>
        </>
    )
}

export default BusinessPage
